import {FC, PropsWithChildren, useCallback, useMemo, useState} from "react";
import {ClientEntity} from "@common-core/coat-operational-hierarchy-appsync-model";
import {
  AllSelectionHandler,
  CreatePlanNode,
  NodeSelectionHandler,
  ToggleAllProcessor,
  ToggleNodeProcessor
} from "../view-model";
import {PlanController} from "./PlanController";
import {PlanContext} from "./PlanContext";

export interface PlanProviderProps {
  /**
   * The ClientEntity from which the hierarchy is being derived
   */
  from: ClientEntity;

  /**
   * The entire hierarchy with the ultimate parent as root
   */
  hierarchy: ClientEntity;

  /**
   * The initial plan to build upon
   */
  initial: CreatePlanNode;

  /**
   * A callback function invoked when node selected is toggled. This function
   * embodies the business rules that determines if selection of the
   * node is allowed.
   *
   * If the function returns a value, the resultant plan is stored in
   * state; otherwise the plan is not updated;
   */
  toggleOne: ToggleNodeProcessor;

  /**
   * A callback function invoked when all node selection is toggled. This function
   * embodies the business rules that determines if selection of the
   * node is allowed.
   *
   * If the function returns a value, the resultant plan is stored in
   * state; otherwise the plan is not updated;
   */
  toggleAll: ToggleAllProcessor;
}

export const PlanProvider: FC<PropsWithChildren<PlanProviderProps>> = ({
  from,
  hierarchy,
  initial,
  toggleAll,
  toggleOne,
  children
}) => {
  const [plan, setPlan] = useState<CreatePlanNode>(initial);

  const onAllSelectionToggle = useCallback<AllSelectionHandler>(
    (event, selected) => {
      const updatedPlan = toggleAll(plan, selected);
      if (updatedPlan) {
        setPlan(updatedPlan);
      }
    },
    [plan, toggleAll]
  );

  const onNodeSelectionToggle = useCallback<NodeSelectionHandler>(
    (event, caId, selected) => {
      const updatedPlan = toggleOne(plan, from.caId, caId, selected);
      if (updatedPlan) {
        setPlan(updatedPlan);
      }
    },
    [plan, from.caId, toggleOne]
  );

  const controller = useMemo<PlanController>(
    () => ({
      from,
      hierarchy,
      plan,
      onAllSelectionToggle,
      onNodeSelectionToggle
    }),
    [from, hierarchy, plan, onAllSelectionToggle, onNodeSelectionToggle]
  );

  return (
    <PlanContext.Provider value={controller}>{children}</PlanContext.Provider>
  );
};
